import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { describeQueryError } from '../../api/describeQueryError.api';
import type { GitSource } from '../../api/admin.api';
import {
  useListAdminCredentialsQuery,
  useListAdminSourcesQuery,
  useTriggerSyncMutation,
} from '../../api/admin.api';
import { ContentState } from '../../components/ContentState';
import { ADMIN_NEW_SOURCE_PATH } from '../../config';
import { formatDateTime } from '../../content/adminDates';
import { HISTORY_VERSIONING, TAG_ADVICE } from '../../content/adminVersioning';
import { cardSurfaceSx, tokens } from '../../theme';

const VERSIONING_TEXT = {
  history: 'its history',
  tags: 'release tags',
  none: 'its release tags once it has any',
} as const;

const statusOf = (source: GitSource): { label: string; problem: boolean } => {
  if ((source.problems?.length ?? 0) > 0) {
    return { label: 'Needs attention', problem: true };
  }

  return source.lastCheckedAt === undefined
    ? { label: 'Not synced yet', problem: false }
    : { label: 'Up to date', problem: false };
};

const SourceCard = ({ source, keyLabel }: { source: GitSource; keyLabel: string | undefined }) => {
  const [trigger, { isLoading, error }] = useTriggerSyncMutation();
  const status = statusOf(source);
  const imported = Object.keys(source.importedRefs ?? {}).length;

  return (
    <Box component="li" sx={{ ...cardSurfaceSx, padding: 5, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'baseline' }}>
          <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: tokens.fontFamily.mono }}>
            {source.id}
          </Typography>
          <Typography variant="body2" sx={{ color: status.problem ? 'error.main' : 'text.secondary', fontWeight: status.problem ? 600 : 400 }}>
            {status.label}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          size="small"
          disabled={isLoading}
          onClick={() => void trigger({ task: 'sync-git-sources', sourceId: source.id })}
          aria-label={`Sync ${source.id}`}
        >
          Sync
        </Button>
      </Box>

      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Added by {source.createdBy}. {keyLabel === undefined ? 'No access key.' : `Access key: ${keyLabel}.`}
      </Typography>

      <Typography variant="body2">
        {source.lastCheckedAt === undefined ? 'Never checked.' : `Last checked ${formatDateTime(source.lastCheckedAt) ?? source.lastCheckedAt}.`}{' '}
        {source.lastChangedAt === undefined ? '' : `Last imported ${formatDateTime(source.lastChangedAt) ?? source.lastChangedAt}. `}
        {/* **A history source has no tags to count**, so its versions are not described as releases. */}
        {source.versioning !== 'history' &&
          (imported === 0 ? 'No releases imported.' : `${String(imported)} release${imported === 1 ? '' : 's'} imported.`)}
      </Typography>

      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Versioned from {VERSIONING_TEXT[source.versioning ?? 'none']}.
      </Typography>

      {source.versioning === 'history' && (
        <Typography variant="body2" role="note" sx={{ color: 'text.secondary' }}>
          {HISTORY_VERSIONING} {TAG_ADVICE}
        </Typography>
      )}

      {(source.problems ?? []).map((problem) => (
        <Typography key={problem} variant="body2" role="alert" sx={{ color: 'error.main' }}>
          {problem}
        </Typography>
      ))}

      {error !== undefined && (
        <Typography variant="body2" role="alert" sx={{ color: 'error.main' }}>
          {describeQueryError(error)}
        </Typography>
      )}
    </Box>
  );
};

/** Every repository pulled in, with its status and a Sync button of its own. */
export const SourcesSection = () => {
  const { data, isLoading, error, refetch } = useListAdminSourcesQuery();
  const { data: credentials } = useListAdminCredentialsQuery();
  const sources = data ?? [];

  return (
    <ContentState
      isLoading={isLoading}
      error={error}
      onRetry={() => void refetch()}
      errorTitle="Unable to load the repositories"
      errorNoun="list of repositories"
      isEmpty={sources.length === 0}
      emptyTitle="No repositories yet"
      emptyMessage="Add one by its address and the toolkit will read its releases."
    >
      <Box component="ul" sx={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {sources.map((source) => (
          <SourceCard
            key={source.id}
            source={source}
            keyLabel={credentials?.find((credential) => credential.id === source.credentialId)?.label}
          />
        ))}
      </Box>
    </ContentState>
  );
};

export const AddSourceButton = () => (
  <Button component={RouterLink} to={ADMIN_NEW_SOURCE_PATH} variant="contained" size="small">
    Add a repository
  </Button>
);
